/**
 * Le dialogue avec la clé de sécurité, côté navigateur.
 *
 * POURQUOI UN MODULE À PART
 * ---------------------------------------------------------------------------
 * L'API WebAuthn parle en `ArrayBuffer`, et JSON n'en transporte pas. Chaque
 * appel demande donc les mêmes conversions, dans les deux sens, sur cinq champs
 * — et une conversion oubliée ne se voit qu'au refus de signature, sur un
 * message qui ne dit pas laquelle.
 *
 * Écrites une fois ici, elles sont justes partout.
 *
 * CE QUE LE SERVEUR ATTEND
 * ---------------------------------------------------------------------------
 * Tout en base64url : c'est ce que le protocole emploie déjà pour les
 * identifiants de clé, et avoir deux conventions dans un même appel est la
 * façon la plus sûre de se tromper de sens.
 */

/** Un tampon d'octets, tel que JSON peut le porter. */
export function versBase64Url(tampon: ArrayBuffer): string {
  const octets = new Uint8Array(tampon);
  let binaire = '';
  for (const octet of octets) {
    binaire += String.fromCharCode(octet);
  }

  return btoa(binaire).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/**
 * Et le chemin inverse, pour ce que le serveur envoie.
 *
 * Le type de retour est `Uint8Array<ArrayBuffer>` et non le `Uint8Array` nu :
 * WebAuthn exige un `BufferSource`, et depuis TypeScript 5.7 un `Uint8Array`
 * sans paramètre peut reposer sur un `SharedArrayBuffer` — que les API du
 * navigateur refusent. Créer le tampon explicitement lève l'ambiguïté au lieu
 * de la contourner par un cast.
 */
export function depuisBase64Url(texte: string): Uint8Array<ArrayBuffer> {
  const complete = texte.replace(/-/g, '+').replace(/_/g, '/');
  const binaire = atob(complete + '='.repeat((4 - (complete.length % 4)) % 4));
  const octets = new Uint8Array(new ArrayBuffer(binaire.length));
  for (let i = 0; i < binaire.length; i++) {
    octets[i] = binaire.charCodeAt(i);
  }

  return octets;
}

/**
 * Le navigateur sait-il faire ?
 *
 * Il faut l'API ET un contexte sécurisé : WebAuthn ne fonctionne qu'en HTTPS,
 * ou sur localhost. Le vérifier avant d'afficher le bouton évite de promettre
 * une fonctionnalité qui échouera au clic sans dire pourquoi.
 */
export function clesPossibles(): boolean {
  return (
    typeof window !== 'undefined'
    && window.isSecureContext
    && typeof window.PublicKeyCredential === 'function'
  );
}

/** Ce que le serveur remet pour préparer un enrôlement. */
export interface DefiEnrolement {
  jeton: string;
  defi: string;
  rpId: string;
  nomSite: string;
  utilisateur: { id: string; nom: string; affichage: string };
  dejaEnrolees: string[];
}

/** Ce que le serveur remet pour demander une signature à la connexion. */
export interface DefiConnexion {
  jeton: string;
  defi: string;
  cles: string[];
}

/**
 * Crée une clé et rend de quoi l'enregistrer.
 *
 * `excludeCredentials` porte les clés déjà enrôlées : sans cette liste, brancher
 * une clé déjà connue l'enrôlerait une seconde fois sous un autre identifiant,
 * et la retirer une fois n'en retirerait qu'une moitié.
 */
export async function creerCle(defi: DefiEnrolement) {
  const creance = (await navigator.credentials.create({
    publicKey: {
      challenge: depuisBase64Url(defi.defi),
      rp: { id: defi.rpId, name: defi.nomSite },
      user: {
        id: depuisBase64Url(defi.utilisateur.id),
        name: defi.utilisateur.nom,
        displayName: defi.utilisateur.affichage,
      },
      // ES256 puis RS256 : les deux seuls algorithmes que le serveur sait
      // vérifier. En proposer d'autres reviendrait à laisser une clé choisir
      // une signature qu'on refusera ensuite, après le geste physique.
      pubKeyCredParams: [
        { type: 'public-key', alg: -7 },
        { type: 'public-key', alg: -257 },
      ],
      authenticatorSelection: { userVerification: 'preferred' },
      excludeCredentials: defi.dejaEnrolees.map((id) => ({
        type: 'public-key' as const,
        id: depuisBase64Url(id),
      })),
      timeout: 120000,
    },
  })) as PublicKeyCredential | null;

  if (creance === null) {
    throw new Error('Aucune clé n’a répondu.');
  }

  const reponse = creance.response as AuthenticatorAttestationResponse;

  return {
    jeton: defi.jeton,
    identifiant: versBase64Url(creance.rawId),
    clientDataJSON: versBase64Url(reponse.clientDataJSON),
    attestationObject: versBase64Url(reponse.attestationObject),
  };
}

/** Demande à la clé de signer le défi de connexion. */
export async function signerConnexion(defi: DefiConnexion) {
  const creance = (await navigator.credentials.get({
    publicKey: {
      challenge: depuisBase64Url(defi.defi),
      allowCredentials: defi.cles.map((id) => ({
        type: 'public-key' as const,
        id: depuisBase64Url(id),
      })),
      userVerification: 'preferred',
      timeout: 120000,
    },
  })) as PublicKeyCredential | null;

  if (creance === null) {
    throw new Error('Aucune clé n’a répondu.');
  }

  const reponse = creance.response as AuthenticatorAssertionResponse;

  return {
    jeton: defi.jeton,
    identifiant: versBase64Url(creance.rawId),
    clientDataJSON: versBase64Url(reponse.clientDataJSON),
    authenticatorData: versBase64Url(reponse.authenticatorData),
    signature: versBase64Url(reponse.signature),
  };
}
